import { useAtom } from "jotai";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { OptionsObject } from "notistack";
import emitSnackbar from "../emitSnackbar";

import * as DeepchatApi from "../api/deepchat";
import {
  ActivityType,
  ChatRequest,
  ChatResponse,
  SalesDataChatRequest,
  ThreadFeedback,
} from "../components/Deepchat/models";
import {
  SessionThreadAtom,
  QuestionIsLoadingAtom,
  ThinkingStepsAtom,
  ThinkingStepsCollapsedAtom,
  QuestionCanAcceptFeedback,
} from "../components/Deepchat/state/DeepchatState";
import { historyAtom } from "../store/home/useRecentThreads";
import { SearchFilters } from "../models/search";
import { SendJsonMessage } from "react-use-websocket/dist/lib/types";
import useUserContext from "../components/Authorization/useUserContext";

const snackbarErrorOptions: OptionsObject = {
  anchorOrigin: { horizontal: "center", vertical: "top" },
  autoHideDuration: 15000,
  preventDuplicate: true,
};

export default function useDeepchat() {
  const user = useUserContext();

  const [session, setSession] = useAtom(SessionThreadAtom);
  const [, setLoading] = useAtom(QuestionIsLoadingAtom);
  const [, setHistory] = useAtom(historyAtom);
  const [, setThinkingSteps] = useAtom(ThinkingStepsAtom);
  const [, setThinkingStepsCollapsed] = useAtom(ThinkingStepsCollapsedAtom);
  const [, setAllowFeedback] = useAtom(QuestionCanAcceptFeedback);

  const navigate = useNavigate();

  const askQuestionStreaming = (
    sendJsonMessage: SendJsonMessage,
    filters: SearchFilters,
    question: string,
    clarificationEnabled?: boolean,
  ) => {
    const sessionKeys = getKeyFromSession(session);

    // Prime the UI for a new turn (reset steps, feedback gate, optimistic pending item) so
    // progress renders immediately rather than when chat.initial lands (~5s later).
    setLoading(true);
    setThinkingSteps([]);
    setThinkingStepsCollapsed(false);
    setAllowFeedback(false);
    setSession((prev) => [
      ...prev,
      {
        user_query: question,
        user_id: "",
        user_chat_session: (sessionKeys ?? "") as ChatResponse["user_chat_session"],
        search_object: filters,
        sources: [],
        pending: true,
      },
    ]);

    navigate(`/deepchat/threads/`);

    sendJsonMessage<WebSocketChatRequest<ChatRequest>>({
      action: "streamChat",
      service_requested: "deepchat",
      user_request: {
        user_chat_session: sessionKeys || null,
        search_request: filters,
        query: question,
        clarification_enabled: Boolean(clarificationEnabled),
      },
    });
  };

  // Not sure if I want to overlap the sale data integration with the core deepchat logic
  const askSalesDataStreaming = (sendJsonMessage: SendJsonMessage, question: string, replace: boolean) => {
    navigate(`/salesdata/session`, { replace });

    const sessionKeys = getKeyFromSession(session);

    // This returns nothing because the consumer is expected to be using { sendJsonMessage, lastJsonMessage } from GetDeepchatSocket()
    sendJsonMessage<WebSocketChatRequest<SalesDataChatRequest>>({
      action: "streamChat",
      service_requested: "structured_data",
      user_request: {
        user_question: question, // ex: "Top 5 growth accounts for last quarter for ocrevus?",
        refresh_sales_cache: false,
      },
    });
  };

  const submitSessionFeedback = (feedback: ThreadFeedback) => {
    return DeepchatApi.postSessionFeedback(feedback, user).catch((error: AxiosError) => {
      console.error("submitSessionFeedback", error);

      // @ts-expect-error Digging error message out response object
      const message = error.response?.data?.detail?.message || "An error ocurred while submitting your feedback.";

      emitSnackbar(message, "error", snackbarErrorOptions);
    });
  };

  const getKeyFromSession = (session: ChatResponse[]) => {
    return session.at(0)?.user_chat_session;
  };

  const getHistory = (activity_type?: ActivityType) => {
    return DeepchatApi.getHistory(user, activity_type);
  };

  const getSession = (sessionTimeStamp: string | number) => {
    return DeepchatApi.getSession(String(sessionTimeStamp), user);
  };

  const renameThread = (userChatSession: string, subject: string) => {
    return DeepchatApi.renameThread(userChatSession, subject, user)
      .then((updatedThread) => {
        // Optimistically update the shared recent-threads list so the sidebar reflects the new
        // title without a refetch. Each thread is a list of responses that all share the subject.
        setHistory((prev) =>
          prev.map((thread) =>
            thread.at(0)?.user_chat_session === userChatSession
              ? thread.map((response) => ({ ...response, subject }))
              : thread,
          ),
        );
        return updatedThread;
      })
      .catch((error: AxiosError) => {
        console.error("renameThread", error);
        emitSnackbar("An error occurred while renaming the thread.", "error", snackbarErrorOptions);
        throw error;
      });
  };

  const deleteThread = (userChatSession: string) => {
    return DeepchatApi.deleteThread(userChatSession, user)
      .then(() => {
        setHistory((prev) => prev.filter((thread) => thread.at(0)?.user_chat_session !== userChatSession));
      })
      .catch((error: AxiosError) => {
        console.error("deleteThread", error);
        emitSnackbar("An error occurred while deleting the thread.", "error", snackbarErrorOptions);
        throw error;
      });
  };

  return {
    askQuestionStreaming,
    submitSessionFeedback,
    getKeyFromSession,
    getHistory,
    getSession,
    renameThread,
    deleteThread,

    askSalesDataStreaming,
  };
}

export interface WebSocketChatResponse<T> {
  type: string;
  data: T;
}

export interface WebSocketChatRequest<T> {
  action: string; // "streamChat"
  service_requested: string; // "deepchat" | "structured_data";
  user_request: T;
}
